import { Suspense, lazy, useCallback, useEffect, useState, type ReactNode } from 'react';
import { BoothGuest } from './views/login/BoothGuest.js';
import { Wall } from './views/ops/Wall.js';
import {
  CountRoomWall,
  DirectorsWall,
  GateKiosk,
  KitchenWall,
  MoneyWallTv,
  NightPhone,
} from './views/ops/Walls.js';
import {
  Chip,
  Shell,
  Toasts,
  closeLive,
  formatDateTime,
  useLive,
  type ShellRail,
  type ShellView,
  type ToastItem,
  type ViewId,
} from '@cider/ui';
import { api, sentence } from './shell/api.js';
import { RAILS } from './shell/nav.js';
import { useNotices, type Notice } from './shell/notices.js';
import { usePhase } from './shell/phase.js';
import { restoreDrafts, useVersion } from './shell/version.js';
import { WhatsNew } from './shell/WhatsNew.js';
import { go, slug, useRoute } from './shell/router.js';
import {
  inFestivalWeekOrder,
  personOf,
  roleName,
  sectionsFor,
  useSession,
  viewsFor,
  type Me,
} from './shell/session.js';
import { ViewAs } from './shell/ViewAs.js';
import { ScreenError } from './shell/ScreenError.js';
import { Account } from './views/login/Account.js';
// The views load when they are opened, not with the sign-in page: the door's script stays
// inside its Lighthouse budget however many screens the views grow (the nightly, Oct 13).
const Decide = lazy(() => import('./views/decide/index.js').then((m) => ({ default: m.View })));
const Admin = lazy(() => import('./views/admin/index.js').then((m) => ({ default: m.View })));
const Money = lazy(() => import('./views/money/index.js').then((m) => ({ default: m.View })));
const Ops = lazy(() => import('./views/ops/index.js').then((m) => ({ default: m.View })));
const Marketing = lazy(() =>
  import('./views/marketing/index.js').then((m) => ({ default: m.View })),
);
const Volunteers = lazy(() =>
  import('./views/volunteers/index.js').then((m) => ({ default: m.View })),
);
const ChairApp = lazy(() => import('./views/chairs/App.js').then((m) => ({ default: m.ChairApp })));
import { VIEW_META } from './views/index.js';
import { PrintPage } from './print/index.js';
import { AnswerPage } from './views/login/Answer.js';
import { BoothDoor } from './views/login/Booth.js';
import { FormFill } from './views/marketing/FormFill.js';
import { Tv } from './views/marketing/Tv.js';
import { Login, doorChoice } from './views/login/Login.js';
import { Onboarding } from './views/login/Onboarding.js';
import { SecondFactor } from './views/login/SecondFactor.js';
import { detroitYear } from './shell/year.js';

function useOnline(): boolean {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const up = (): void => {
      setOnline(true);
    };
    const down = (): void => {
      setOnline(false);
    };
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return online;
}

/**
 * **One install per surface** (the ninth overnight, item 3): the booth, the chair app, Watchtower
 * and the count room each install under their own name, opening where they work. The page in view
 * picks its manifest; everything else installs as CIDER. One icon for now — each surface's own
 * icon waits on its artwork.
 */
export function surfaceOf(path: string): 'booth' | 'chair' | 'watchtower' | 'count' | null {
  if (path.startsWith('/booth')) return 'booth';
  if (path.startsWith('/app')) return 'chair';
  if (path.startsWith('/wall') || path.startsWith('/ops/watchtower')) return 'watchtower';
  if (path.startsWith('/money/cash-bank/count')) return 'count';
  return null;
}
function useSurfaceManifest(path: string): void {
  useEffect(() => {
    const link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    const surface = surfaceOf(path);
    if (link) link.href = surface ? `/manifest-${surface}.webmanifest` : '/manifest.webmanifest';
  }, [path]);
}

/** The document title, per screen: "Where we stand · Volunteers · CIDER". */
function Title({ text }: { text: string }): ReactNode {
  useEffect(() => {
    document.title = text;
  }, [text]);
  return null;
}
const titleCase = (upper: string): string =>
  upper.toLowerCase().replace(/(^|\s)\S/g, (c) => c.toUpperCase());

/** The shell's view list: every view, with its items from the registry through the policy. */
/** The pages that are their own: inside the shell, under no view (walk fixes 2, item 5). */
const STANDALONE = new Set(['decide', 'whats-new', 'account']);

function shellViews(me: Me): ShellView[] {
  const person = personOf(me);
  return (Object.keys(RAILS) as ViewId[]).map((id) => ({
    id,
    name: VIEW_META[id].name,
    desc: VIEW_META[id].desc,
    items: sectionsFor(person, id).map((s) => ({
      label: s.title,
      href: `/${id}/${slug(s.title)}`,
    })),
  }));
}

export function App(): ReactNode {
  const online = useOnline();
  const route = useRoute();
  useSurfaceManifest(route.path);
  const { loading, me, refresh, offline } = useSession();
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [viewAsOpen, setViewAsOpen] = useState(false);
  const phase = usePhase();
  const notices = useNotices(me?.signedIn === true && me.kind === 'person');
  const version = useVersion();
  const [seenVersion, setSeenVersion] = useState<string | null | undefined>(undefined);
  // A live reconnect: the version may have moved while we were away.
  useLive(me?.signedIn === true && me.kind === 'person' ? '/api/events' : null, [], (sig) => {
    if (sig.kind === 'reconnected') version.check();
  });
  // A typed draft kept through a reload comes back once the fields exist.
  useEffect(() => {
    restoreDrafts();
  }, []);

  const toast = useCallback((kind: ToastItem['kind'], text: string) => {
    setToasts((t) => [...t, { id: String(Date.now() + Math.random()), kind, text }]);
  }, []);
  const signOut = useCallback(async () => {
    await api('POST', '/auth/sign-out').catch(() => undefined);
    closeLive('/api/events');
    await refresh();
    go('/login');
  }, [refresh]);

  if (route.page === 'print') return <PrintPage kind={route.section} id={route.screen} />;
  // The invitation's answer page needs nobody signed in (Oct 2): the link is the credential.
  if (route.page === 'answer' && route.token)
    return <AnswerPage token={route.token} choice={route.section} />;
  // ADR 0034: a small form's page — the link is the key, so it never waits for a session.
  if (route.page === 'form' && route.token) return <FormFill token={route.token} />;
  // K2a: a signage screen — its key is the screen's own, so it never waits for a session.
  if (route.page === 'tv' && route.token) return <Tv screenKey={route.token} />;
  if (loading) return <div className="boot">Opening CIDER…</div>;
  // The guest screen beside a station: paired by the station's own session, never a sign-in.
  if (route.page === 'booth-guest') return <BoothGuest me={me} />;
  // The booth door always answers (overnight 6): signed out, signed in, or a station.
  if (route.page === 'booth') return <BoothDoor token={route.token} me={me} onOpened={refresh} />;
  // A check-in station is never shown a person's screens: wherever it lands, it lands at its door.
  if (
    me?.signedIn &&
    me.kind === 'device' &&
    (me.device?.kind === 'booth' || me.device?.kind === 'door')
  ) {
    go('/booth');
    return null;
  }
  if (!me?.signedIn) {
    if (route.page !== 'login') go('/login');
    return <Login route={route} onSignedIn={refresh} />;
  }
  // The second step never dead-ends (Sep 25): it shows what the account actually holds, and
  // where it holds nothing it is the enrollment itself.
  const factorsSet = me.factorsSet ?? { passkey: false, totp: false };
  if (me.secondFactor === 'pending' && (factorsSet.passkey || factorsSet.totp))
    return <SecondFactor mode="pending" has={factorsSet} onDone={refresh} onSignOut={signOut} />;
  if (me.mustSetSecondFactor || me.secondFactor === 'pending')
    return <SecondFactor mode="set_one" has={factorsSet} onDone={refresh} onSignOut={signOut} />;
  // The first sign-in's card (People Part 5): once, before the app opens.
  if (me.onboarding === 'due')
    return (
      <Onboarding
        onDone={async (opensAt) => {
          await refresh();
          go(opensAt);
        }}
        onSignOut={signOut}
      />
    );

  const person = personOf(me);
  const views = shellViews(me);
  const mine = viewsFor(me);
  /**
   * Where a sign-in lands.
   *
   * "Sign in → the app" writes its choice to the browser, and what reads it is the chair app's
   * own route group. Until that lands there is nothing for the app lane to go to that the
   * person would not already have reached: a chair's home IS the chair app, so a chair lands
   * there either way, and a director's home is Decide.
   *
   * The first attempt at this read the choice against the views the person can *see* rather
   * than the one they belong in, and a director can see every view (ruling 91) — so every
   * director who picked the app lane was thrown into the chairs view, which is what walk 01
   * caught. Who a person is decides where they land; a button cannot grant a home.
   *
   * **Now there is somewhere for the app lane to go.** `/app` is the chair app's own address,
   * so a chair lands there, and so does anybody who picked that lane and holds a chair's role.
   */
  const chairsHome = me.home === 'today';
  const wantsApp = doorChoice() === 'app' && (chairsHome || me.viewingAs != null);
  /**
   * **The chair app's own host** (James, Oct 13 — D0): chair.applefestmi.com, and
   * chair.next.applefestmi.com on staging. Opening it never shows the operator shell: every
   * address that is not the chair app (or its paper) opens the chair app, and sign-in lands on
   * Today (paper — `/print` — has already returned above). A director there gets "You direct the
   * festival" and picks an area.
   */
  const onChairHost = window.location.hostname.startsWith('chair.');
  if (onChairHost && route.page !== 'app') {
    go('/app/today');
    return null;
  }
  const home = wantsApp
    ? '/app'
    : me.home === 'decide'
      ? '/decide'
      : me.home === 'portal' || me.home === 'device' || me.home === 'none'
        ? '/decide'
        : chairsHome
          ? '/app'
          : `/${mine[0] ?? 'admin'}`;
  if (route.page === 'login' || route.page === 'unknown') {
    go(home);
    return null;
  }

  /**
   * The chair app is its own surface, and it is returned **before** the shell is built: no
   * rail, no switcher, no six views, nothing of Festival Admin around it (James, Sep 27). A
   * director reaches it the same way, through View as a chair, and sees the same screens under
   * the drawing's read-only banner.
   */
  // The wall: one screen for the TV in the Watchtower room — no rail, no switcher.
  if (route.page === 'wall')
    return route.section === 'money' ? (
      <MoneyWallTv />
    ) : route.section === 'count-room' ? (
      <CountRoomWall />
    ) : route.section === 'directors' ? (
      <DirectorsWall />
    ) : route.section === 'kitchen' ? (
      <KitchenWall />
    ) : route.section === 'gate' ? (
      <GateKiosk />
    ) : route.section === 'night' ? (
      <NightPhone />
    ) : (
      <Wall />
    );
  if (route.page === 'app')
    return (
      <Suspense fallback={null}>
        <ChairApp
          me={me}
          tab={route.section}
          screen={route.screen}
          offline={!online}
          onSignOut={() => {
            void api('POST', '/auth/sign-out').finally(() => {
              window.location.assign('/login');
            });
          }}
        />
      </Suspense>
    );

  const view: ViewId = route.view ?? mine[0] ?? 'admin';
  const sections = inFestivalWeekOrder(
    sectionsFor(person, view, { byAddress: true }),
    view,
    phase.id,
  );
  // What's new (People Part 6): the bell's dot until this version's page is opened.
  const lastSeen = seenVersion === undefined ? (me.seenVersion ?? null) : seenVersion;
  const unseenVersion =
    version.current && version.current.version !== lastSeen ? version.current.version : null;
  const season = phase.festivalYear ?? me.roles?.[0]?.festivalYear ?? detroitYear();
  // The home of a view is the first screen of its first section; a section's home is its first screen.
  if (route.page === 'view' && sections.length > 0 && view !== 'chairs') {
    // A director opening Money lands on the director's Money (James, Oct 15 — ruling 5); the rail
    // stays the finance director's.
    const director = (me.roles ?? []).some((r) => r.role === 'director' && r.active !== false);
    const today = sections.find((s) => slug(s.title) === 'today');
    if (
      view === 'money' &&
      !route.section &&
      director &&
      today?.screens.some((x) => slug(x.title) === 'director')
    ) {
      go('/money/today/director');
      return null;
    }
    const sec = sections.find((s) => slug(s.title) === route.section) ?? null;
    const first = sec ?? sections[0];
    if (first && (!sec || !route.screen) && first.screens[0]) {
      go(`/${view}/${slug(first.title)}/${slug(first.screens[0].title)}`);
      return null;
    }
  }
  const currentSection = sections.find((s) => slug(s.title) === route.section) ?? null;
  const sectionTitle = currentSection?.title;
  const currentScreen =
    currentSection?.screens.find((sc) => slug(sc.title) === route.screen) ?? null;
  const rail: ShellRail | undefined = currentSection
    ? {
        section: currentSection.title,
        screens: currentSection.screens.map((sc) => ({
          label: sc.title,
          href: `/${view}/${slug(currentSection.title)}/${slug(sc.title)}`,
          current: sc.title === currentScreen?.title,
          why: sc.why,
        })),
      }
    : undefined;
  const canDecide = person.assignments.some((a) => a.role === 'director');

  let body: ReactNode;
  if (route.page === 'whats-new') body = <WhatsNew onSeen={setSeenVersion} />;
  else if (route.page === 'account') body = <Account me={me} onSignedIn={refresh} />;
  else if (route.page === 'decide')
    body = <Decide canDecide={canDecide} meId={me.personId ?? null} />;
  else if (view === 'money')
    body = (
      <Money
        me={me}
        name={VIEW_META.money.name}
        charter={VIEW_META.money.charter}
        season={season}
        sections={sections}
        section={route.section}
        screen={route.screen}
        onGo={go}
      />
    );
  else if (view === 'admin')
    body = (
      <Admin
        me={me}
        name={VIEW_META.admin.name}
        charter={VIEW_META.admin.charter}
        season={season}
        sections={sections}
        section={route.section}
        screen={route.screen}
        onGo={go}
      />
    );
  else if (view === 'ops')
    body = (
      <Ops
        me={me}
        name={VIEW_META.ops.name}
        charter={VIEW_META.ops.charter}
        season={season}
        sections={sections}
        section={route.section}
        screen={route.screen}
        onGo={go}
      />
    );
  else if (view === 'marketing')
    body = (
      <Marketing
        me={me}
        name={VIEW_META.marketing.name}
        charter={VIEW_META.marketing.charter}
        season={season}
        sections={sections}
        section={route.section}
        screen={route.screen}
        onGo={go}
      />
    );
  // `/chairs` was a telephone drawn inside the director's shell — a rail on the left, a
  // switcher on top, and a 390-wide rectangle in the middle pretending to be a phone. It is
  // gone (James, Sep 27). The chair app is a surface at `/app`, and this address goes there so
  // an old link, a bookmark or a notification still lands somewhere real.
  else if (view === 'chairs') {
    go(route.section ? `/app/${route.section}` : '/app');
    return null;
  } else
    body = (
      <Volunteers
        me={me}
        name={VIEW_META.volunteers.name}
        charter={VIEW_META.volunteers.charter}
        season={season}
        sections={sections}
        section={route.section}
        screen={route.screen}
        onGo={go}
      />
    );

  return (
    <>
      <Title
        text={
          route.page === 'decide'
            ? 'Decide · CIDER'
            : route.page === 'whats-new'
              ? 'What’s new · CIDER'
              : `${currentScreen?.title ?? currentSection?.title ?? VIEW_META[view].charter} · ${titleCase(VIEW_META[view].name)} · CIDER`
        }
      />
      <Shell
        views={views}
        view={STANDALONE.has(route.page) ? (mine[0] ?? 'admin') : view}
        // Decide and What's new are pages of their own: the shell keeps its bar and its phase
        // strip, and shows no other view's sections or rail (walk fixes 2, item 5).
        standalone={
          route.page === 'decide'
            ? { name: 'Decide' }
            : route.page === 'whats-new'
              ? { name: 'What’s new' }
              : route.page === 'account'
                ? { name: 'Your account' }
                : undefined
        }
        section={STANDALONE.has(route.page) ? undefined : sectionTitle}
        screen={STANDALONE.has(route.page) ? undefined : currentScreen?.title}
        rail={STANDALONE.has(route.page) || view === 'chairs' ? undefined : rail}
        phase={{ id: phase.id, countdown: phase.countdown, tone: phase.tone }}
        who={{
          initial: (me.name ?? me.email ?? me.device?.label ?? '?').charAt(0).toUpperCase(),
          name: me.name ?? me.email ?? me.device?.label ?? 'Device',
          role: roleName(me),
          views: mine,
          // The queue arrives with Money and Volunteers; until then the badge is NO DATA, not 0.
          decide: null,
          // The bell reads /comms/notifications (defect #64); NO DATA until the first answer. A new version adds one.
          unread: notices.count === null ? null : notices.count + (unseenVersion ? 1 : 0),
        }}
        status={
          version.current
            ? {
                version: version.current.version,
                build: version.current.build,
                deployedAt: version.current.deployedAt,
                href: '/whats-new',
              }
            : undefined
        }
        newVersion={
          version.fresh ? { version: version.fresh.version, onReload: version.reload } : undefined
        }
        viewingAs={
          me.viewingAs
            ? {
                // Names, never ids (overnight 6, 3b): the banner printed both ids.
                name: me.viewingAs.chairName ?? 'the chair',
                area: me.viewingAs.areaName ?? 'their area',
                endsAt: me.viewingAs.until ?? me.session?.expiresAt ?? '',
              }
            : undefined
        }
        onGo={go}
        onViewAs={
          person.assignments.some((a) => a.role === 'director' || a.role === 'volunteer_chair') &&
          !me.viewingAs
            ? () => {
                setViewAsOpen(true);
              }
            : undefined
        }
        onStopViewingAs={
          me.viewingAs
            ? () => {
                api('DELETE', '/admin/view-as')
                  .then(refresh)
                  .catch((e: unknown) => {
                    toast('bad', sentence(e));
                  });
              }
            : undefined
        }
        onSignOut={() => void signOut()}
        onAccount={() => {
          go('/account');
        }}
        offline={!online || offline}
        jump={(q, close) => <Jump query={q} me={me} close={close} />}
        bell={(close) => (
          <Bell
            notices={notices.notices}
            unseenVersion={unseenVersion}
            close={close}
            onDone={(id) => {
              notices.done(id).catch((e: unknown) => {
                toast('bad', sentence(e));
              });
            }}
          />
        )}
      >
        {/* No screen is ever blank: a body that throws keeps the shell and says so (Walk fixes 1). */}
        <ScreenError screen={route.path}>
          <Suspense fallback={null}>{body}</Suspense>
        </ScreenError>
      </Shell>
      {viewAsOpen ? (
        <ViewAs
          onCancel={() => {
            setViewAsOpen(false);
          }}
          onDone={async () => {
            setViewAsOpen(false);
            await refresh();
            go('/app');
            toast('warn', 'Viewing as a chair — read-only, audited, one hour.');
          }}
        />
      ) : null}
      <Toasts
        items={toasts}
        onClose={(id) => {
          setToasts((t) => t.filter((x) => x.id !== id));
        }}
      />
    </>
  );
}

/** The bell's list: one row per event, the newest first, a link to the thing, Done drops it. */
function Bell({
  notices,
  unseenVersion,
  close,
  onDone,
}: {
  notices: Notice[] | null;
  unseenVersion: string | null;
  close: () => void;
  onDone: (id: string) => void;
}): ReactNode {
  if (notices === null)
    return (
      <p className="cider-shell__notice-empty">
        <Chip kind="no-data" /> The notices could not be read.
      </p>
    );
  if (notices.length === 0 && !unseenVersion)
    return <p className="cider-shell__notice-empty">Nothing waiting on you.</p>;
  return (
    <ul className="cider-shell__notices">
      {unseenVersion ? (
        <li className="cider-shell__notice" data-testid="whats-new-notice">
          <span className="cider-shell__notice-body">
            <a
              href="/whats-new"
              onClick={(e) => {
                e.preventDefault();
                close();
                go('/whats-new');
              }}
            >
              What’s new in {unseenVersion}
            </a>
            <span className="cider-shell__notice-when">a new version since you last looked</span>
          </span>
        </li>
      ) : null}
      {notices.map((n) => (
        <li key={n.id} className="cider-shell__notice">
          <span className="cider-shell__notice-body">
            {n.href ? (
              <a
                href={n.href}
                onClick={(e) => {
                  e.preventDefault();
                  close();
                  go(n.href ?? '/');
                }}
              >
                {n.title}
              </a>
            ) : (
              n.title
            )}
            <span className="cider-shell__notice-when">
              {n.kind} · {formatDateTime(n.createdAt)}
            </span>
          </span>
          <button
            type="button"
            className="cider-shell__notice-done"
            aria-label={`Done: ${n.title}`}
            onClick={() => {
              onDone(n.id);
            }}
          >
            Done
          </button>
        </li>
      ))}
    </ul>
  );
}

/** Jump-to, Stage 0b: screens by plain words from the rails. People, vendors and shifts arrive with their modules. */
interface SearchHit {
  kind: 'sponsor' | 'organization' | 'person' | 'family';
  id: string;
  name: string;
  says: string;
  href: string;
}

/**
 * The things the festival knows, for the Jump box (Sponsors Part 5): a sponsor by its business
 * name, a contact's name or their email; a person; a family by surname. The API trims what comes
 * back to the doors the searcher holds, so a name never appears that a screen would refuse.
 */
function useThings(query: string): SearchHit[] {
  const [hits, setHits] = useState<SearchHit[]>([]);
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setHits([]);
      return;
    }
    let live = true;
    const t = setTimeout(() => {
      api<{ hits: SearchHit[] }>('GET', `/search?q=${encodeURIComponent(q)}`)
        .then((r) => {
          if (live) setHits(r.hits);
        })
        .catch(() => {
          if (live) setHits([]);
        });
    }, 180);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [query]);
  return hits;
}

function Jump({ query, me, close }: { query: string; me: Me; close: () => void }): ReactNode {
  const person = personOf(me);
  const things = useThings(query);
  const q = query.trim().toLowerCase();
  const hits = (Object.keys(RAILS) as ViewId[]).flatMap((v) =>
    sectionsFor(person, v).flatMap((s) =>
      s.screens
        .filter(
          (sc) =>
            q.length > 0 &&
            (sc.title.toLowerCase().includes(q) ||
              (sc.was ?? '').toLowerCase().includes(q) ||
              (sc.also ?? '').toLowerCase().includes(q) ||
              s.title.toLowerCase().includes(q)),
        )
        .map((sc) => ({
          view: v,
          section: s.title,
          screen: sc.title,
          href: `/${v}/${slug(s.title)}/${slug(sc.title)}`,
        })),
    ),
  );
  return (
    <div className="jump">
      {q.length === 0 ? (
        <p className="jump__note">
          Type a screen’s name. People, vendors and shifts join the results as their modules land{' '}
          <Chip kind="no-data" />.
        </p>
      ) : hits.length === 0 && things.length === 0 ? (
        <p className="jump__note">
          Nothing by that name yet. <b>“I can’t find…”</b> will send your words to the volunteer
          chairs once comms lands.
        </p>
      ) : (
        hits.slice(0, 12).map((h) => (
          <a
            key={h.href}
            href={h.href}
            onClick={(e) => {
              e.preventDefault();
              close();
              go(h.href);
            }}
          >
            <span>{h.screen}</span>
            <span className="jump__path">
              {VIEW_META[h.view].name} → {h.section}
            </span>
          </a>
        ))
      )}
      {things.map((t) => (
        <a
          key={`${t.kind}:${t.id}`}
          href={t.href}
          onClick={(e) => {
            e.preventDefault();
            close();
            go(t.href);
          }}
        >
          <span>{t.name}</span>
          <span className="jump__path">{t.says}</span>
        </a>
      ))}
    </div>
  );
}
